import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Auth flow & route guards. Read teacher state from useTeacher() (TanStack
// Query); no AuthProvider context needed for the read path.
import ProtectedRoute from "./auth/ProtectedRoute";
import PublicRoute from "./auth/PublicRoute";
import OnboardingRoute from "./auth/OnboardingRoute";

// Top-level safety net for uncaught render errors so a single
// component crash doesn't blank the entire app.
import ErrorBoundary from "./components/ui/ErrorBoundary";

// Pages that ship in the LANDING-PAGE bundle (everything an unauthenticated
// visitor or a teacher pre-onboarding can hit without crossing a code-
// split boundary). Keeping these eager-loaded means the first-paint
// experience stays snappy on the marketing surfaces — the dominant
// volume by far. Verified 2026-05-24: main JS bundle was 1.34 MB
// uncompressed before code-splitting because the entire dashboard +
// every tool was bundled into the entry chunk.
import HomePage from "./pages/landing/HomePage";
import LandingPage from "./pages/landing/LandingPage";
import ManitobaLandingPage from "./pages/landing/ManitobaLandingPage";
import BCLandingPage from "./pages/landing/BCLandingPage";
import PublicRubricPage from "./pages/public/PublicRubricPage";
import LoginPage from "./pages/auth/LoginPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import TermsPage from "./pages/legal/TermsPage";
import ContactPage from "./pages/legal/ContactPage";

// Pages that ship in their own lazy chunks — fetched only when an
// authenticated teacher (or post-magic-link signup) actually navigates
// to them. Lazy imports return Promises that React.lazy unwraps via
// the <Suspense> boundary below.
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const OnboardingPage = lazy(() => import("./pages/onboarding/OnboardingPage"));
const HistoryPage = lazy(() => import("./pages/dashboard/HistoryPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const BillingReturnPage = lazy(() => import("./pages/billing/BillingReturnPage"));
const ToolPlaceholderPage = lazy(() => import("./pages/dashboard/ToolPlaceholderPage"));

// MVP Tool Pages — each gets its own chunk so e.g. a Manitoba teacher
// never downloads the BC tool's code and vice versa.
const LearningSkillsPage = lazy(() => import("./pages/dashboard/learning-skills/LearningSkillsPage"));
const RubricPage = lazy(() => import("./pages/dashboard/rubric/RubricPage"));
const KindergartenPage = lazy(() => import("./pages/dashboard/kindergarten/KindergartenPage"));
const WorksheetsPage = lazy(() => import("./pages/dashboard/worksheets/WorksheetsPage"));
const ManitobaPage = lazy(() => import("./pages/dashboard/manitoba/ManitobaPage"));
const BCPage = lazy(() => import("./pages/dashboard/bc/BCPage"));
const OntarioSubjectPage = lazy(() => import("./pages/dashboard/ontario-subject-comments/OntarioSubjectPage"));

// Lightweight fallback for the Suspense boundary. Matches the body
// background so the brief gap on chunk-load looks like a hold, not a
// flash of broken UI.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-stone-50" aria-busy="true" aria-live="polite" />
  );
}

// Route-level gate that respects the PostHog `tool_killswitch` flag.
// Wrapping each tool route means a disabled tool's page is never
// mounted (no useQuery fires, no banner-vs-page hooks ordering issue).
import ToolGuard from "./components/tool/ToolGuard";
// Two-source gate for worksheets: dev hostname (SHOW_WORKSHEETS) OR
// the `worksheets_enabled` PostHog flag. Lets us graduate worksheets
// to production via the flag without removing the dev-host shortcut.
import WorksheetsGate from "./components/tool/WorksheetsGate";

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      {/*
        Marketing surface area:
          /                          → HomePage. Broad, K through 12. Brand level.
          /ontario                   → redirect to the LS deep page.
          /ontario/learning-skills   → LandingPage. Hyper specific, Ontario LS,
                                       single ICP, single CTA. Where Meta ad
                                       traffic for the LS campaign lands.
        Future campaign pages (e.g. /tools/rubric) get added the same way.
      */}
      <Route path="/" element={<HomePage />} />
      <Route
        path="/ontario"
        element={<Navigate to="/ontario/learning-skills" replace />}
      />
      <Route
        path="/ontario/learning-skills"
        element={
          <LandingPage
            forcedRegionCode="CA-ON"
            focus="ONTARIO_LEARNING_SKILLS"
          />
        }
      />
      <Route path="/manitoba/learning-behaviours" element={<ManitobaLandingPage />} />
      <Route path="/bc/proficiency-scale-comments" element={<BCLandingPage />} />

      {/* Legal pages — required for launch (Stripe checkout, K-12 trust). */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* /pricing → HomePage #pricing anchor. Gives a shareable URL for
           the pricing block (Twitter/email links) without maintaining
           a duplicate page. */}
      <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />

      {/* /contact → mailto fallback. Kept as a real route so external
           links don't 404. */}
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* /signup is an alias for /login. Same Stytch Google OAuth flow
           handles both new account creation and returning sign-in; we
           keep a separate URL because all our marketing CTAs ("Sign up
           free", "Try it free") link teachers here, and a teacher
           landing on a 404 is a launch-blocker. The LoginPage's copy
           reads neutrally enough to serve both new and returning users. */}
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Onboarding. Authenticated but not onboarded. */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />

      {/* Protected Routes. Requires auth + onboarding. */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Launch tools. Each is gated with <ToolGuard> so ops can
          disable a single tool from PostHog without a redeploy:
          flip `tool_killswitch` to the tool's id and teachers see
          the unavailable banner; flip back to `none` to restore. */}
      <Route
        path="/dashboard/learning-skills"
        element={
          <ProtectedRoute>
            <ToolGuard tool="learning_skills" name="Learning Skills">
              <LearningSkillsPage />
            </ToolGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/rubric"
        element={
          <ProtectedRoute>
            <ToolGuard tool="rubric" name="Rubric Builder">
              <RubricPage />
            </ToolGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/kindergarten"
        element={
          <ProtectedRoute>
            <ToolGuard tool="kindergarten" name="Kindergarten Comments">
              <KindergartenPage />
            </ToolGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/manitoba"
        element={
          <ProtectedRoute>
            <ToolGuard tool="manitoba" name="Manitoba Learning Behaviours">
              <ManitobaPage />
            </ToolGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/bc"
        element={
          <ProtectedRoute>
            <ToolGuard tool="bc_subject" name="BC Subject Comments">
              <BCPage />
            </ToolGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/ontario-subject"
        element={
          <ProtectedRoute>
            <ToolGuard tool="ontario_subject" name="Ontario Subject Comments">
              <OntarioSubjectPage />
            </ToolGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/classes"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/worksheets"
        element={
          <ProtectedRoute>
            <WorksheetsGate>
              <ToolGuard tool="worksheets" name="Worksheets">
                <WorksheetsPage />
              </ToolGuard>
            </WorksheetsGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/worksheets/:id"
        element={
          <ProtectedRoute>
            <WorksheetsGate>
              <ToolGuard tool="worksheets" name="Worksheets">
                <WorksheetsPage />
              </ToolGuard>
            </WorksheetsGate>
          </ProtectedRoute>
        }
      />

      {/* History */}
      <Route
        path="/dashboard/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Billing return (after Stripe checkout) */}
      <Route
        path="/billing/return"
        element={
          <ProtectedRoute>
            <BillingReturnPage />
          </ProtectedRoute>
        }
      />

      {/* Coming-soon placeholder for retired/in-progress tools */}
      <Route
        path="/coming-soon"
        element={
          <ProtectedRoute>
            <ToolPlaceholderPage />
          </ProtectedRoute>
        }
      />

      {/* Public lead-magnet tools. No auth, no save. Acquisition surface. */}
      <Route path="/tools/rubric" element={<PublicRubricPage />} />

      {/* Any other dashboard/tool URL → coming-soon */}
      <Route
        path="/dashboard/*"
        element={<Navigate to="/coming-soon" replace />}
      />
      <Route
        path="/tools/*"
        element={<Navigate to="/coming-soon" replace />}
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  // Providers (BrowserRouter, QueryClientProvider, AuthProvider) live in
  // ./AppProviders.tsx and are mounted in index.tsx. App is just routes,
  // wrapped in an ErrorBoundary so an uncaught crash inside any page
  // gets a recovery UI ("Reload page" / "Try again") instead of a
  // blank white screen.
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <AppRoutes />
      </Suspense>
    </ErrorBoundary>
  );
}
